import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';
import { invalidateDataUploadFiles } from 'src/data-capture/hooks/file-uploads/invalidateDataUploadFiles';
import { UploadedFile } from 'src/data-capture/hooks/file-uploads/types';

type UseFamilyFileActionsOptions = {
  familyId: string;
};

const ownerResource = (file: UploadedFile, familyId: string) => {
  if (file.owner?.kind === 'family' || !file.clientId) {
    return `families/${familyId}/data-upload-files`;
  }
  return `clients/${file.clientId}/data-upload-files`;
};

export const useFamilyFileActions = ({ familyId }: UseFamilyFileActionsOptions) => {
  const queryClient = useQueryClient();
  const invalidate = (file: UploadedFile) =>
    invalidateDataUploadFiles(queryClient, { clientId: file.clientId, familyId });

  const {
    mutateAsync: deleteFileMutation,
    error: deleteError,
    reset: resetDeleteError,
    isPending: isDeleting,
  } = useMutation<unknown, Error, UploadedFile>({
    mutationFn: file => api.delete(`${ownerResource(file, familyId)}/${file.id}`),
    onSuccess: (_data, file) => invalidate(file),
  });

  const moveFile = async (file: UploadedFile, parentId: string | null | undefined) => {
    await api.put(`${ownerResource(file, familyId)}/${file.id}`, {
      parentId: parentId ?? null,
      isResolved: file.isResolved,
      isFiled: true,
      fileName: null,
      documentDate: file.documentDate ?? null,
    });
    await invalidate(file);
  };

  const renameFolder = async (file: UploadedFile, newName: string) => {
    await api.put(`${ownerResource(file, familyId)}/${file.id}`, {
      isResolved: file.isResolved,
      isFiled: false,
      parentId: file.parentId ?? null,
      fileName: newName,
    });
    await invalidate(file);
  };

  const updateDocumentDate = async (file: UploadedFile, documentDate: Date) => {
    await api.put(`${ownerResource(file, familyId)}/${file.id}`, {
      documentDate: documentDate.toISOString().split('T')[0],
      isResolved: file.isResolved,
      parentId: file.parentId ?? null,
      isFiled: file.file.isFiled,
    });
    await invalidate(file);
  };

  const setFileResolved = async (file: UploadedFile, isResolved: boolean) => {
    await api.put(`${ownerResource(file, familyId)}/${file.id}`, {
      isResolved,
      isFiled: false,
      parentId: file.parentId ?? null,
      fileName: null,
      documentDate: file.documentDate ?? null,
    });
    await invalidate(file);
  };

  const deleteFile = async (file: UploadedFile) => {
    await deleteFileMutation(file);
  };

  const {
    mutateAsync: dismissMutation,
    isPending: isDismissing,
    variables: dismissingVariables,
  } = useMutation<unknown, Error, { file: UploadedFile; clientId: string }>({
    mutationFn: ({ file, clientId }) =>
      api.post(`families/${familyId}/data-upload-files/${file.id}/members/${clientId}/dismiss`),
    onSuccess: (_data, { file }) => invalidate(file),
  });

  const dismissDetectedClient = (file: UploadedFile, clientId: string) =>
    dismissMutation({ file, clientId });

  const { mutateAsync: finaliseMemberMutation } = useMutation<
    unknown,
    Error,
    { file: UploadedFile; clientId: string }
  >({
    mutationFn: ({ file, clientId }) =>
      api.post(`families/${familyId}/data-upload-files/${file.id}/members/${clientId}/finalise`),
    onSuccess: (_data, { file }) => invalidate(file),
  });

  const finaliseMember = (file: UploadedFile, clientId: string) =>
    finaliseMemberMutation({ file, clientId });

  const { mutateAsync: retryProcessingMutation, isPending: isRetryingProcessing } = useMutation<
    { data: UploadedFile },
    Error,
    UploadedFile
  >({
    mutationFn: file =>
      api.post<UploadedFile>(`${ownerResource(file, familyId)}/${file.id}/retry-processing`),
    onSuccess: (_data, file) => invalidate(file),
  });

  const retryProcessing = async (file: UploadedFile) => {
    await retryProcessingMutation(file);
  };

  return {
    deleteFile,
    deleteError,
    resetDeleteError,
    isDeleting,
    moveFile,
    renameFolder,
    updateDocumentDate,
    setFileResolved,
    dismissDetectedClient,
    isDismissing,
    dismissingClientId: isDismissing ? (dismissingVariables?.clientId ?? null) : null,
    finaliseMember,
    retryProcessing,
    isRetryingProcessing,
  };
};
